import { ref, computed } from 'vue';
import { useEventListener, useWindowSize } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

// The conversation list pane can be dragged wider or narrower on its edge,
// the same way the main sidebar can (owner order 2026-09-16). The width is a
// per-user UI setting, so it survives reloads and devices; nothing on the
// server changes shape. Below `md` the pane is full width and the handle is
// hidden.
export const CONVERSATION_LIST_DEFAULT_WIDTH = 340;
export const CONVERSATION_LIST_MIN_WIDTH = 260;
export const CONVERSATION_LIST_MAX_WIDTH = 640;
const SETTING_KEY = 'conversation_list_width';

const clamp = width =>
  Math.max(
    CONVERSATION_LIST_MIN_WIDTH,
    Math.min(CONVERSATION_LIST_MAX_WIDTH, Math.round(width))
  );

export function useConversationListResize() {
  const { uiSettings, updateUISettings } = useUISettings();
  const isRTL = useMapGetter('accounts/isRTL');
  const { width: windowWidth } = useWindowSize();
  const isMobile = computed(() => windowWidth.value < 768);

  // the stock layout was 340px, 412px from the 2xl breakpoint; keep that as
  // the default until the user drags
  const defaultWidth = computed(() =>
    windowWidth.value >= 1536 ? 412 : CONVERSATION_LIST_DEFAULT_WIDTH
  );
  const savedWidth = uiSettings.value?.[SETTING_KEY];
  const listWidth = ref(savedWidth ? clamp(savedWidth) : defaultWidth.value);
  const isResizing = ref(false);
  const startX = ref(0);
  const startWidth = ref(0);

  const getClientX = event =>
    event.touches ? event.touches[0].clientX : event.clientX;

  const onResizeStart = event => {
    if (isMobile.value) return;
    isResizing.value = true;
    startX.value = getClientX(event);
    startWidth.value = listWidth.value;
    Object.assign(document.body.style, {
      cursor: 'col-resize',
      userSelect: 'none',
    });
    event.preventDefault();
  };

  const onResizeMove = event => {
    if (!isResizing.value) return;
    // the handle sits on the inline-end edge: dragging away from the main
    // sidebar widens the list in both directions
    const delta = isRTL.value
      ? startX.value - getClientX(event)
      : getClientX(event) - startX.value;
    listWidth.value = clamp(startWidth.value + delta);
  };

  const onResizeEnd = () => {
    if (!isResizing.value) return;
    isResizing.value = false;
    Object.assign(document.body.style, { cursor: '', userSelect: '' });
    updateUISettings({ [SETTING_KEY]: listWidth.value });
  };

  const resetWidth = () => {
    listWidth.value = defaultWidth.value;
    updateUISettings({ [SETTING_KEY]: null });
  };

  useEventListener(document, 'mousemove', onResizeMove);
  useEventListener(document, 'mouseup', onResizeEnd);
  useEventListener(document, 'touchmove', onResizeMove, { passive: false });
  useEventListener(document, 'touchend', onResizeEnd);

  const listStyle = computed(() =>
    isMobile.value ? undefined : { width: `${listWidth.value}px` }
  );

  return {
    listWidth,
    listStyle,
    isResizing,
    isMobile,
    onResizeStart,
    resetWidth,
  };
}
